import React, { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { formatFileSize } from '../../utils/imageCompression';
import { ATTACHMENT_HREF_PREFIX } from '../../utils/workItemAttachmentMarkup';
import './MarkdownViewer.scss';

// linkTickets:把內文的 #123 渲染成可點的 Ticket 連結。預設開(舊 Ticket 沿用),
// 工作項目傳 false —— 那邊的關聯改成在 Drawer 右側手動設定,打 # 只是提個編號,
// 不該長成連結讓人以為建立了關聯(見 WorkItemReferences)。
// attachments:Map<id, {file_name, file_size}>,給留言附件 [檔名](wi-attachment:id) 用。
//   有給且查不到該 id → 渲染成灰色「(已移除)」不可點,不要吐死連結。
//   沒給(null)→ 當成還沒載入,照常渲染可點,不要誤標已移除。
// onAttachmentClick:點擊時才即時換 signed URL 下載(URL 一小時過期,不能寫進內文)。
const MarkdownViewer = ({ content = '', className = '', maxLines = null, onTicketClick = null, linkTickets = true, attachments = null, onAttachmentClick = null }) => {
  const containerRef = useRef(null);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  // Event delegation: 攔截 ticket-ref 和 img 點擊
  const handleClick = useCallback((e) => {
    // Ticket reference 點擊
    const ticketRef = e.target.closest('.ticket-ref');
    if (ticketRef) {
      e.preventDefault();
      e.stopPropagation();
      const no = parseInt(ticketRef.dataset.ticketNo, 10);
      if (no && onTicketClick) onTicketClick(no);
      return;
    }
    // 留言附件 chip 點擊
    const attachRef = e.target.closest('.wi-attach-ref');
    if (attachRef) {
      e.preventDefault();
      e.stopPropagation();
      if (attachRef.classList.contains('is-missing')) return; // 已移除 → 不可點
      const id = parseInt(attachRef.dataset.attachmentId, 10);
      if (id && onAttachmentClick) onAttachmentClick(id);
      return;
    }
    // Image lightbox（stopPropagation：避免冒泡到外層容器（如描述區）觸發編輯）
    const img = e.target.closest('img');
    if (img?.src) {
      e.preventDefault();
      e.stopPropagation();
      setLightboxSrc(img.src);
    }
  }, [onTicketClick, onAttachmentClick]);

  // ESC 關閉 lightbox
  useEffect(() => {
    if (!lightboxSrc) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setLightboxSrc(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [lightboxSrc]);
  // 將 Markdown 轉換為 HTML
  const htmlContent = useMemo(() => {
    if (!content) return '';

    try {
      // 清理 Toast UI Editor 複製貼上後的格式問題
      // 移除多餘的 HTML 標籤，統一用 markdown 處理
      let cleanedContent = content
        .replace(/<br\s*\/?>/gi, '\n')  // 把 <br> 轉成換行
        .replace(/@\[([^\]]+)\]\(([0-9a-f-]{36})\)/gi, '[$1]($2)')  // 移除 mention 前綴 @，renderer 會加回
        .trim();

      // #ticket_no 自動連結：跳過 code block / inline code 內的 #\d+
      if (linkTickets) {
        cleanedContent = cleanedContent.replace(
          /(```[\s\S]*?```|`[^`]+`)|(^|[^\d])#(\d+)(?=[\s\n).,;:!?]|$)/gm,
          (match, codeBlock, prefix, ticketNo) => {
            if (codeBlock) return codeBlock;
            return `${prefix}[#${ticketNo}](ticket:${ticketNo})`;
          }
        );
      }

      // 保留多行空白：Markdown 會把連續空行壓成一個段落分隔，
      // 這裡把每個額外的空行轉成 &nbsp; 佔位段落
      cleanedContent = cleanedContent.replace(/\n{3,}/g, (match) => {
        const extra = match.length - 2;
        return '\n\n' + '\u00A0\n\n'.repeat(extra);
      });
      
      // 自訂 renderer，讓所有連結都在新分頁開啟
      const renderer = new marked.Renderer();
      const originalLinkRenderer = renderer.link.bind(renderer);

      // marked v5+ 改為物件參數 { href, title, text, tokens }
      renderer.link = function(token) {
        const href = token.href || '';
        const text = token.text || '';
        // @mention 連結：href 是 UUID 格式，渲染成不可點擊的高亮 tag
        if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(href)) {
          return `<span class="mention-tag">@${text}</span>`;
        }
        // 留言附件：href 是 wi-attachment:<id>。內文只存 id,檔名 / 大小以 attachments 為準
        // (檔名可能已被改名);查不到 = 已移除,渲染成不可點的灰字。
        if (href.startsWith(ATTACHMENT_HREF_PREFIX)) {
          const id = href.slice(ATTACHMENT_HREF_PREFIX.length);
          const meta = attachments ? attachments.get(Number(id)) : undefined;
          const missing = attachments != null && !meta;
          const name = meta?.file_name || text || '檔案';
          const size = meta?.file_size ? ` · ${formatFileSize(meta.file_size)}` : '';
          const safeName = name.replace(/[<>&"]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' }[c]));
          return `<span class="wi-attach-ref${missing ? ' is-missing' : ''}" data-attachment-id="${id}">`
            + `<span class="wi-attach-icon">📎</span>`
            + `<span class="wi-attach-name">${safeName}</span>`
            + `<span class="wi-attach-meta">${missing ? '（已移除）' : size}</span>`
            + `</span>`;
        }
        // #ticket 連結：href 是 ticket:數字 格式
        if (href.startsWith('ticket:')) {
          const no = href.slice(7);
          return `<span class="ticket-ref" data-ticket-no="${no}">#${no}</span>`;
        }
        const html = originalLinkRenderer(token);
        return html.replace(/^<a /, '<a target="_blank" rel="noopener noreferrer" ');
      };

      // 配置 marked 選項
      marked.setOptions({
        breaks: true,        // 支援換行（GFM 風格）
        gfm: true,           // 啟用 GitHub Flavored Markdown
        headerIds: false,    // 不產生 header id
        mangle: false,       // 不轉換 email
        renderer: renderer,  // 使用自訂的 renderer
      });

      // 將 Markdown 轉為 HTML
      const rawHtml = marked.parse(cleanedContent);

      // 使用 DOMPurify 清理 HTML，但保留圖片的 data: URL 和 style 屬性
      let cleanHtml = DOMPurify.sanitize(rawHtml, {
        ALLOWED_TAGS: [
          'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
          'p', 'br', 'hr',
          'strong', 'em', 'del', 'code', 'pre',
          'ul', 'ol', 'li',
          'blockquote',
          'table', 'thead', 'tbody', 'tr', 'th', 'td',
          'a', 'img',
          'span', 'div', // 允許 span 和 div（用於顏色）
          'input', // 允許 input（用於 task list checkbox）
        ],
        ALLOWED_ATTR: [
          'href', 'title', 'alt', 'src',
          'class', 'style', // 允許 style 屬性（用於顏色）
          'target', 'rel',
          'type', 'checked', 'disabled', // 允許 checkbox 相關屬性
          'data-ticket-no', // 允許 ticket reference 的 data 屬性
          'data-attachment-id', // 允許留言附件 chip 的 data 屬性
        ],
        // 允許 data: URL（用於 Base64 圖片）
        ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|data):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
      });

      // Post-processing: 在 DOMPurify 之後將 task list 轉換成 checkbox
      // 支援兩種格式：
      // 1. 標準 GFM: "- [ ] text" 或 "* [ ]"（中間有空格）
      // 2. 簡化版: "- [] text" 或 "* []"（中間沒空格）
      cleanHtml = cleanHtml
        // 未勾選：[] 或 [ ]（後面可能有空格，也可能沒有）
        .replace(/<li>\[\s?\]\s*(.+?)<\/li>/g, '<li class="task-list-item"><input type="checkbox" disabled> $1</li>')
        // 已勾選：[x] 或 [X]
        .replace(/<li>\[x\]\s*(.+?)<\/li>/gi, '<li class="task-list-item"><input type="checkbox" checked disabled> $1</li>');

      return cleanHtml;
    } catch (error) {
      console.error('Markdown 轉換錯誤:', error);
      return '<p style="color: red;">內容格式錯誤</p>';
    }
  }, [content, linkTickets, attachments]);

  // 如果沒有內容，返回空
  if (!content) {
    return null;
  }

  return (
    <>
      <div
        ref={containerRef}
        className={`markdown-viewer ${className} ${maxLines ? 'line-clamp' : ''}`}
        style={maxLines ? { '--max-lines': maxLines } : {}}
        dangerouslySetInnerHTML={{ __html: htmlContent }}
        onClick={handleClick}
      />
      {lightboxSrc && createPortal(
        <div className="markdown-lightbox-overlay" onClick={(e) => { e.stopPropagation(); setLightboxSrc(null); }}>
          <img
            src={lightboxSrc}
            alt=""
            className="markdown-lightbox-image"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            className="markdown-lightbox-close"
            onClick={(e) => { e.stopPropagation(); setLightboxSrc(null); }}
            aria-label="關閉"
          >
            ✕
          </button>
        </div>,
        document.body
      )}
    </>
  );
};

export default MarkdownViewer;
